/**
 * <s-theme-toggle> — Light / dark colour mode switch.
 *
 * Commands (dispatched via commandfor/command on descendant buttons):
 *   --toggle-theme — Flip <html data-theme> between light and dark and remember the choice.
 *
 * The saved choice is applied before first paint by the inline bootstrap in layout/theme.liquid,
 * so this element only syncs aria-pressed and handles the toggle. Several toggles (header and
 * mobile menu) stay in sync through the `gv:theme-change` window event.
 */
const STORAGE_KEY = 'gv-theme';
const CHANGE_EVENT = 'gv:theme-change';

class SThemeToggle extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#sync();

    this.addEventListener('command', (event) => {
      if (event.command !== '--toggle-theme') return;
      const root = document.documentElement;
      const next = root.dataset.theme === 'dark' ? 'light' : 'dark';
      root.dataset.theme = next;
      try { localStorage.setItem(STORAGE_KEY, next); } catch { /* storage unavailable */ }
      window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { theme: next } }));
    }, { signal });

    window.addEventListener(CHANGE_EVENT, () => this.#sync(), { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #sync() {
    const isDark = document.documentElement.dataset.theme === 'dark';
    this.querySelector('button')?.setAttribute('aria-pressed', String(isDark));
  }
}

if (!customElements.get('s-theme-toggle')) customElements.define('s-theme-toggle', SThemeToggle);
